import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Trophy, Target, Zap, ChevronRight, Play, Star, Crown, 
  BookOpen, Dumbbell, Palette, Loader2, Lock, Calendar, TrendingUp, Users
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import LeadCapture from '../components/LeadCapture';
import PowerScoreShareCard from '../components/PowerScoreShareCard';
import MeetTheDirector from '../components/MeetTheDirector';

const AssessmentResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [improvement, setImprovement] = useState(null);

  // Get results from navigation state or redirect
  const results = location.state;

  // Save Power Score on mount
  useEffect(() => {
    if (results && !scoreSaved) {
      savePowerScore();
    }
  }, [results]);

  const savePowerScore = async () => {
    if (!results) return;
    
    try {
      const response = await api.post('/academy/power-score/save', {
        email: results.userInfo.email,
        name: results.userInfo.name,
        academic_score: results.categoryScores.academic,
        tactical_score: results.categoryScores.sports,
        brand_score: results.categoryScores.brand,
        total_score: results.score,
        major: results.major.name,
        cohort: "Winter '26"
      });
      
      setScoreSaved(true);
      
      if (response.data.is_retake) {
        setImprovement({
          points: response.data.improvement,
          percent: response.data.improvement_pct,
          previous: response.data.previous_score
        });
        toast.success(`Score improved by ${response.data.improvement} points! 📈`);
      }
    } catch (error) {
      console.error('Failed to save power score:', error);
    }
  };

  useEffect(() => {
    if (!results) {
      // No results in state, redirect to diagnostic
      navigate('/academy/legacy-diagnostic');
    }
  }, [results, navigate]);

  if (!results) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const { score, major, recruitability, potential, categoryScores, userInfo } = results;

  // Score color based on value
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-orange-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  const handleCheckout = async (option) => {
    setLoading(true);
    setSelectedOption(option);
    
    try {
      const checkoutData = {
        name: userInfo.name,
        email: userInfo.email,
        origin_url: window.location.origin,
        assessment_score: score,
        major: major.name
      };

      if (option === 'entry') {
        // $49/month - The Combine Full Access
        const response = await api.post('/academy/assessment-checkout', {
          ...checkoutData,
          checkout_type: 'combine_subscription',
          amount: 49,
          tier: 'the_combine'
        });
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } else if (option === 'deposit') {
        // $100 deposit for Spring '26 Seat
        const response = await api.post('/academy/assessment-checkout', {
          ...checkoutData,
          checkout_type: 'deposit',
          deposit_type: 'spring_26_seat'
        });
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-16 pb-12">
      {/* Improvement Banner (if retake) */}
      {improvement && (
        <div className="bg-gradient-to-r from-green-600/20 via-green-500/20 to-green-600/20 border-b border-green-500/30 py-4 px-4">
          <div className="max-w-4xl mx-auto text-center flex items-center justify-center gap-3">
            <TrendingUp className="w-6 h-6 text-green-500" />
            <p className="text-green-400 font-bold">
              Score improved by {improvement.points} points ({improvement.percent > 0 ? '+' : ''}{improvement.percent}%) from your previous {improvement.previous}!
            </p>
          </div>
        </div>
      )}
      
      {/* Hero Section - Score Reveal */}
      <div className="bg-gradient-to-b from-gray-900 via-gray-900/50 to-black py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Major Badge */}
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold tracking-wider"
              style={{ backgroundColor: `${major.color}20`, color: major.color, border: `1px solid ${major.color}40` }}>
              <span className="text-xl">{major.icon}</span>
              {major.name.toUpperCase()}
            </span>
          </div>

          {/* Score Display */}
          <div className="relative inline-block mb-6">
            <div className="w-48 h-48 relative">
              {/* Background circle */}
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-gray-800"
                />
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={`${(score / 100) * 553} 553`}
                  className="text-orange-500 transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-5xl font-black ${getScoreColor(score)}`}>{score}</span>
                <span className="text-gray-500 text-sm font-bold tracking-wider">LEGACY SCORE</span>
              </div>
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-black mb-2">
            {userInfo.name}, You're <span style={{ color: major.color }}>{major.name}</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            {major.description}
          </p>
        </div>
      </div>

      {/* Recruitability vs Potential */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-500" />
              YOUR CURRENT POSITION
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recruitability */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm font-medium">Current Recruitability</span>
                  <span className="text-white font-bold">{recruitability}%</span>
                </div>
                <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 transition-all duration-1000"
                    style={{ width: `${recruitability}%` }}
                  />
                </div>
                <p className="text-gray-600 text-xs mt-2">How coaches see you today</p>
              </div>

              {/* Potential */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm font-medium">Elite Potential</span>
                  <span className="text-orange-500 font-bold">{potential}%</span>
                </div>
                <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-1000"
                    style={{ width: `${potential}%` }}
                  />
                </div>
                <p className="text-gray-600 text-xs mt-2">Where you could be with the Playbook</p>
              </div>
            </div>

            {/* Gap Highlight */}
            <div className="mt-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <p className="text-orange-500 font-bold text-center">
                📈 Close the {potential - recruitability}% gap with the High Performance Playbook
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
            <BookOpen className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-black text-white">{categoryScores.academic}%</p>
            <p className="text-gray-500 text-sm">Academic</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
            <Dumbbell className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-black text-white">{categoryScores.sports}%</p>
            <p className="text-gray-500 text-sm">Sports IQ</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
            <Palette className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-black text-white">{categoryScores.brand}%</p>
            <p className="text-gray-500 text-sm">Brand</p>
          </div>
        </div>
      </div>

      {/* Share Your Score Card */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <PowerScoreShareCard
          userName={userInfo.name}
          totalScore={score}
          academicScore={categoryScores.academic}
          tacticalScore={categoryScores.sports}
          brandScore={categoryScores.brand}
          major={major.name}
          cohort="Winter '26"
        />
      </div>

      {/* Video Teaser - REPLACED WITH ELECTIVE PREVIEW CAROUSEL */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Path Discovery Header with Fade-in */}
        <div className="text-center mb-10 animate-fadeIn">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight">
            PATH <span className="text-orange-500">DISCOVERY</span>
          </h2>
          <p className="text-gray-400 text-lg">
            Curated electives tailored to your performance profile
          </p>
        </div>
        
        {/* Elective Preview Carousel */}
        <div className="relative overflow-hidden rounded-2xl" style={{ background: 'linear-gradient(180deg, #000000 0%, #1A1A1A 100%)' }}>
          <div className="py-10 px-4">
            {/* Horizontal Scroller */}
            <div className="flex gap-10 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* Featured Electives */}
              {[
                { id: 'strategic-thinking', title: 'Strategic Thinking', icon: '👑', color: '#A855F7', desc: 'Develop strategic foresight and pattern recognition across all domains.' },
                { id: 'branding-principles', title: 'Branding Principles', icon: '🎨', color: '#F97316', desc: 'Build a brand that outlasts your playing days. Visual identity & storytelling.' },
                { id: 'nil-deep-dive', title: 'NIL Deep Dive', icon: '📄', color: '#EAB308', desc: 'The definitive playbook for personal monetization and contract analysis.' },
                { id: 'entrepreneurship', title: 'Entrepreneurship', icon: '💼', color: '#22C55E', desc: 'Transition from player to owner. Launch ventures that leverage your platform.' },
                { id: 'casting-preparedness', title: 'Casting Preparedness', icon: '🎬', color: '#3B82F6', desc: 'Every interview is an audition. Master camera presence & media training.' }
              ].map((elective, index) => (
                <div 
                  key={elective.id}
                  className="flex-none w-72 snap-center transform transition-all duration-500 hover:scale-105"
                  style={{
                    animation: `slideInElective 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  <div 
                    className="h-full p-6 rounded-xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl"
                    style={{ 
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {/* Icon */}
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl mb-4"
                      style={{ backgroundColor: `${elective.color}20` }}
                    >
                      {elective.icon}
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-lg font-bold text-white mb-2">{elective.title}</h3>
                    
                    {/* Description */}
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">{elective.desc}</p>
                    
                    {/* Preview Tag */}
                    <div 
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: `${elective.color}20`, color: elective.color }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: elective.color }}></span>
                      PREVIEW
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Elevated Standards Banner */}
          <div className="absolute bottom-0 left-0 right-0 py-4 px-6 bg-gradient-to-t from-black via-black/95 to-transparent">
            <div className="flex items-center justify-center gap-3">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-orange-500/50 to-transparent"></div>
              <p className="text-gray-400 text-xs uppercase tracking-[0.25em] font-medium whitespace-nowrap">
                Choreographing Your Full Experience — Video Premiere Coming Soon
              </p>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-orange-500/50 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>

      {/* The Pitch */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="bg-gradient-to-r from-orange-500/10 via-orange-600/5 to-orange-500/10 border border-orange-500/30 rounded-xl p-8 text-center">
          <Zap className="w-10 h-10 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">
            Your baseline is set. Now it's time to <span className="text-orange-500">own the game.</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            To reach Elite status, you need the <span className="text-orange-500 font-bold">High Performance Playbook</span>. 
            Our proven system has helped 200+ scholar-athletes close their gap and dominate their recruiting journey.
          </p>
        </div>
      </div>

      {/* Checkout Options */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-xl font-black text-center mb-8 text-white tracking-wider">
          CLOSE THE DEAL
        </h2>

        {/* Director's Executive Guarantee - Before Checkout */}
        <div className="mb-10 max-w-xl mx-auto text-center">
          <MeetTheDirector 
            variant="executive" 
            isParent={false}
            showBlurb={true}
          />
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Option A: The Entry - Updated with Elevated Features */}
          <Card className="bg-gray-900 border-2 border-gray-800 hover:border-orange-500/50 transition-all duration-300 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Option A: The Entry</p>
                  <h3 className="text-xl font-black text-white">Enter The Combine</h3>
                </div>
              </div>
              
              <div className="mb-6">
                <span className="text-4xl font-black text-white">$49</span>
                <span className="text-gray-500">/month</span>
                <p className="text-xs text-gray-500 mt-1">Full Combine Access</p>
              </div>
              
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <div className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Target className="w-3 h-3 text-orange-500" />
                  </div>
                  <div>
                    <span className="font-semibold text-white">Personalized Learning Path</span>
                    <p className="text-gray-500 text-xs mt-0.5">Adaptive skill-building tailored to your performance</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <div className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <TrendingUp className="w-3 h-3 text-orange-500" />
                  </div>
                  <div>
                    <span className="font-semibold text-white">Real-Time Analytics Dashboard</span>
                    <p className="text-gray-500 text-xs mt-0.5">Instant visibility into progress and proficiency growth</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <div className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <BookOpen className="w-3 h-3 text-orange-500" />
                  </div>
                  <div>
                    <span className="font-semibold text-white">Comprehensive Resource Library</span>
                    <p className="text-gray-500 text-xs mt-0.5">Unlimited access to standard-aligned curriculum materials</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-gray-300 text-sm">
                  <div className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Users className="w-3 h-3 text-orange-500" />
                  </div>
                  <div>
                    <span className="font-semibold text-white">Teacher-Led Support Sync</span>
                    <p className="text-gray-500 text-xs mt-0.5">Periodic syncs with certified facilitators</p>
                  </div>
                </li>
              </ul>

              <Button
                onClick={() => handleCheckout('entry')}
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-6"
                data-testid="checkout-entry-btn"
              >
                {loading && selectedOption === 'entry' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    ENTER THE COMBINE
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Option B: Deposit */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-amber-500/50 hover:border-amber-500 transition-all duration-300 overflow-hidden relative">
            <div className="absolute top-0 right-0 bg-amber-500 text-black px-4 py-1 text-xs font-black">
              LIMITED SPOTS
            </div>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Option B: The Roster Spot</p>
                  <h3 className="text-xl font-black text-white">Reserve Your Spring '26 Seat</h3>
                </div>
              </div>
              
              <div className="mb-6">
                <span className="text-4xl font-black text-white">$100</span>
                <span className="text-gray-500"> deposit</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  Guaranteed Spring '26 Cohort Seat
                </li>
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <Lock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  Branding & Strategy Electives Added
                </li>
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <Star className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  Priority Early Access
                </li>
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <Zap className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  Deposit Applied to Full Enrollment
                </li>
              </ul>

              <Button
                onClick={() => handleCheckout('deposit')}
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-6"
                data-testid="checkout-deposit-btn"
              >
                {loading && selectedOption === 'deposit' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    RESERVE MY SEAT
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Trust Elements */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 text-sm">
            🔒 Secure checkout powered by Stripe • Cancel anytime
          </p>
        </div>
        
        {/* Not Ready? Join the Roster */}
        <div className="max-w-2xl mx-auto mt-12 pt-12 border-t border-gray-800">
          <div className="text-center mb-6">
            <p className="text-gray-500 text-sm uppercase tracking-wider">Not ready to commit?</p>
          </div>
          <LeadCapture 
            source="assessment_exit" 
            variant="default"
            title="Stay on the Radar"
            subtitle="Join the roster for priority access when you're ready"
          />
        </div>
      </div>
    </div>
  );
};

export default AssessmentResults;
